"use client";

import { useSearchParams } from "next/navigation";
import type { Workspace } from "@/services/workspace-service";
import { useWorkspaceStore } from "@/store/workspace/workspace-store";
import { handleWorkspaceNotFound } from "@/utils/workspace";

const WORKSPACE_QUERY_KEY = "workspace_id";
const LEGACY_WORKSPACE_QUERY_KEY = "workspace";

type SearchParamsReader = {
  get: (name: string) => string | null;
};

type ActiveWorkspaceSource = "url" | "store" | "none";

type WorkspaceResolutionState = {
  workspaces: Workspace[];
  selectedWorkspace: Workspace | null;
  workspacesLoaded: boolean;
};

export interface ActiveWorkspaceContext {
  workspace: Workspace | null;
  workspaceId?: string;
  organizationId?: string;
  source: ActiveWorkspaceSource;
  workspacesLoaded: boolean;
}

function normalizeWorkspaceId(value?: string | null): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

export function getWorkspaceIdFromSearchParams(
  searchParams?: SearchParamsReader | null,
): string | null {
  const workspaceId = normalizeWorkspaceId(
    searchParams?.get(WORKSPACE_QUERY_KEY),
  );
  if (workspaceId) return workspaceId;

  return normalizeWorkspaceId(searchParams?.get(LEGACY_WORKSPACE_QUERY_KEY));
}

export function getWorkspaceIdFromLocation(search?: string): string | null {
  if (typeof window === "undefined" && !search) {
    return null;
  }

  const params = new URLSearchParams(search ?? window.location.search);
  return getWorkspaceIdFromSearchParams(params);
}

function findWorkspaceById(
  workspaces: Workspace[],
  workspaceId?: string | null,
): Workspace | null {
  if (!workspaceId) return null;
  return workspaces.find((workspace) => workspace.workspace_id === workspaceId) ?? null;
}

function buildWorkspaceContext(
  state: WorkspaceResolutionState,
  source: ActiveWorkspaceSource,
  workspace: Workspace | null,
  workspaceId?: string | null,
): ActiveWorkspaceContext {
  const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);
  return {
    workspace,
    workspaceId: normalizedWorkspaceId ?? workspace?.workspace_id,
    organizationId: workspace?.organization_id,
    source,
    workspacesLoaded: state.workspacesLoaded,
  };
}

function resolveWorkspaceContext(
  state: WorkspaceResolutionState,
  workspaceIdFromUrl?: string | null,
): ActiveWorkspaceContext {
  const normalizedWorkspaceIdFromUrl = normalizeWorkspaceId(workspaceIdFromUrl);
  const selectedWorkspace = state.selectedWorkspace;

  if (normalizedWorkspaceIdFromUrl) {
    if (selectedWorkspace?.workspace_id === normalizedWorkspaceIdFromUrl) {
      return buildWorkspaceContext(
        state,
        "url",
        selectedWorkspace,
        normalizedWorkspaceIdFromUrl,
      );
    }

    const workspaceFromUrl = findWorkspaceById(
      state.workspaces,
      normalizedWorkspaceIdFromUrl,
    );
    if (workspaceFromUrl) {
      return buildWorkspaceContext(
        state,
        "url",
        workspaceFromUrl,
        normalizedWorkspaceIdFromUrl,
      );
    }

    // Keep an explicit URL workspace only until we have real workspace data
    // to validate it against. Once the list is available, fall back to the
    // selected workspace instead of scoping requests to a bad/stale id.
    if (state.workspaces.length === 0) {
      return buildWorkspaceContext(
        state,
        "url",
        null,
        normalizedWorkspaceIdFromUrl,
      );
    }
  }

  return buildWorkspaceContext(
    state,
    selectedWorkspace ? "store" : "none",
    selectedWorkspace,
  );
}

export function resolveActiveWorkspaceContext(): ActiveWorkspaceContext {
  return resolveWorkspaceContext(
    useWorkspaceStore.getState(),
    getWorkspaceIdFromLocation(),
  );
}

export function syncWorkspaceStoreToLocation(): Workspace | null {
  const state = useWorkspaceStore.getState();
  const workspaceIdFromUrl = getWorkspaceIdFromLocation();
  const workspaceFromUrl = findWorkspaceById(state.workspaces, workspaceIdFromUrl);

  if (!workspaceFromUrl) {
    return state.selectedWorkspace;
  }

  const selectedWorkspace = state.selectedWorkspace;
  const isAlreadySelected =
    selectedWorkspace?.workspace_id === workspaceFromUrl.workspace_id &&
    selectedWorkspace?.organization_id === workspaceFromUrl.organization_id;

  if (!isAlreadySelected) {
    const ok = state.setSelectedWorkspace(workspaceFromUrl);
    if (!ok) {
      handleWorkspaceNotFound();
    }
  }

  return workspaceFromUrl;
}

export function getActiveWorkspaceScopeKey(): string {
  const { workspaceId, workspacesLoaded } = resolveWorkspaceContext(
    useWorkspaceStore.getState(),
    getWorkspaceIdFromLocation(),
  );
  if (!workspacesLoaded) return "pending";
  return workspaceId ?? "none";
}

export function getResolvedWorkspaceId(
  workspaceId?: string | null,
): string | null {
  const normalizedWorkspaceId = normalizeWorkspaceId(workspaceId);
  if (normalizedWorkspaceId) {
    return normalizedWorkspaceId;
  }

  return normalizeWorkspaceId(resolveActiveWorkspaceContext().workspaceId);
}

export function buildWorkspaceAwarePath(
  pathname: string,
  params?: URLSearchParams,
  workspaceId?: string | null,
): string {
  const nextParams = new URLSearchParams(params?.toString() ?? "");
  const resolvedWorkspaceId = getResolvedWorkspaceId(workspaceId);

  if (resolvedWorkspaceId) {
    nextParams.set(WORKSPACE_QUERY_KEY, resolvedWorkspaceId);
  }

  const queryString = nextParams.toString();
  return queryString ? `${pathname}?${queryString}` : pathname;
}

export function useActiveWorkspaceScopeKey(): string {
  const { workspaceId, workspacesLoaded } = useActiveWorkspaceContext();

  if (!workspacesLoaded) return "pending";
  return workspaceId ?? "none";
}

/** Reactive equivalent of resolveActiveWorkspaceContext for client components. */
export function useActiveWorkspaceContext(): ActiveWorkspaceContext {
  const searchParams = useSearchParams();
  const workspaces = useWorkspaceStore((state) => state.workspaces);
  const selectedWorkspace = useWorkspaceStore((state) => state.selectedWorkspace);
  const workspacesLoaded = useWorkspaceStore((state) => state.workspacesLoaded);
  return resolveWorkspaceContext(
    {
      workspaces,
      selectedWorkspace,
      workspacesLoaded,
    },
    getWorkspaceIdFromSearchParams(searchParams),
  );
}
